/**
 * Pure structural edits on a template document (R2 builder).
 *
 * Add, move, and delete blocks — the operations that turn "edit a template's config" into
 * "author one". They live here, not in the web app, for two reasons: they are about the template
 * DOCUMENT shape (not the UI), and they are the kind of tree surgery that is easy to get subtly
 * wrong, so they belong somewhere a Node suite can exercise them exhaustively.
 *
 * Every function is IMMUTABLE — it returns a new template and never mutates its input. The editor
 * holds template state in React; an in-place edit there would not re-render, and a shared
 * sub-object would let one edit silently alter an unrelated part of the tree.
 *
 * ── How a block is addressed ──
 *
 * By a numeric CHAIN: `[pageIndex, i0, i1, ...]`. `i0` indexes the page's `blocks`; each further
 * index descends into the addressed block's `children`. So `[2, 3, 1]` is
 * `pages[2].blocks[3].children[1]`. The web outline's dotted string paths convert to this with
 * `chainFromPath`.
 */
import type { BlockInstanceT, BlockManifest } from './template-schema.js';

type Template = Record<string, any>;

/** Every integer in a dotted outline path, in order — the first is the page index. */
export function chainFromPath(path: string): number[] {
  return path.split('.').filter((p) => /^\d+$/.test(p)).map(Number);
}

/** The block at a chain, or undefined if the chain does not resolve. */
export function blockAt(tpl: Template, chain: number[]): BlockInstanceT | undefined {
  if (chain.length === 0) return undefined;
  const [pageIndex, ...rest] = chain;
  let blocks: BlockInstanceT[] | undefined = tpl.pages?.[pageIndex]?.blocks;
  let block: BlockInstanceT | undefined;
  for (const idx of rest) {
    if (!blocks) return undefined;
    block = blocks[idx];
    blocks = block?.children;
  }
  return block;
}

/**
 * Apply a function to the blocks ARRAY that holds the addressed block, immutably.
 *
 * The chain's last index is the position within that array; everything before it locates the
 * array. The callback receives a copy of the array and the index, and returns the new array.
 * Every container from the page down is shallow-cloned, so React sees a changed reference at each
 * level it needs to and nowhere else.
 */
function updateSiblings(
  tpl: Template,
  chain: number[],
  fn: (siblings: BlockInstanceT[], index: number) => BlockInstanceT[],
): Template {
  if (chain.length < 2) return tpl;
  const [pageIndex, ...rest] = chain;
  const pages = [...(tpl.pages ?? [])];
  const page = { ...pages[pageIndex] };
  pages[pageIndex] = page;

  if (rest.length === 1) {
    page.blocks = fn([...(page.blocks ?? [])], rest[0]);
    return { ...tpl, pages };
  }

  // Descend to the parent container, cloning each block on the way.
  page.blocks = [...(page.blocks ?? [])];
  let blocks: BlockInstanceT[] = page.blocks;
  for (let i = 0; i < rest.length - 2; i++) {
    const idx = rest[i];
    const b = { ...blocks[idx] } as BlockInstanceT;
    b.children = [...(b.children ?? [])];
    blocks[idx] = b;
    blocks = b.children;
  }
  const parentIdx = rest[rest.length - 2];
  const parent = { ...blocks[parentIdx] } as BlockInstanceT;
  parent.children = fn([...(parent.children ?? [])], rest[rest.length - 1]);
  blocks[parentIdx] = parent;
  return { ...tpl, pages };
}

/** Remove the block at a chain. */
export function deleteBlock(tpl: Template, chain: number[]): Template {
  return updateSiblings(tpl, chain, (siblings, index) => {
    const next = [...siblings];
    next.splice(index, 1);
    return next;
  });
}

/**
 * Move a block among its siblings by `delta` (-1 up, +1 down).
 *
 * Clamped: a block already first cannot move up, last cannot move down — the call is a no-op
 * rather than an error or a wrap-around, because a reorder button at the end of a list should do
 * nothing, not teleport the block to the top.
 */
export function moveBlock(tpl: Template, chain: number[], delta: number): Template {
  return updateSiblings(tpl, chain, (siblings, index) => {
    const target = index + delta;
    if (target < 0 || target >= siblings.length) return siblings;
    const next = [...siblings];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
}

/** Insert a block immediately after the one at a chain, as a sibling. */
export function insertBlockAfter(tpl: Template, chain: number[], block: BlockInstanceT): Template {
  return updateSiblings(tpl, chain, (siblings, index) => {
    const next = [...siblings];
    next.splice(index + 1, 0, block);
    return next;
  });
}

/** Append a block to a container's children, or to a page's blocks when the chain is a page. */
export function appendBlock(tpl: Template, chain: number[], block: BlockInstanceT): Template {
  if (chain.length === 1) {
    const pages = [...(tpl.pages ?? [])];
    const page = { ...pages[chain[0]] };
    page.blocks = [...(page.blocks ?? []), block];
    pages[chain[0]] = page;
    return { ...tpl, pages };
  }
  // Append into the addressed block's own children — reuse updateSiblings by targeting a
  // phantom index at the end of its children.
  const target = blockAt(tpl, chain);
  const childCount = target?.children?.length ?? 0;
  // Build a chain that addresses a position inside the block's children, then insert-after the
  // last child, or seed an empty children array when there are none.
  if (childCount === 0) {
    return updateSelf(tpl, chain, (b) => ({ ...b, children: [block] }));
  }
  return insertBlockAfter(tpl, [...chain, childCount - 1], block);
}

/** Replace the block at a chain with the result of `fn`, immutably. */
export function updateSelf(
  tpl: Template, chain: number[], fn: (b: BlockInstanceT) => BlockInstanceT,
): Template {
  if (chain.length < 2) return tpl;
  return updateSiblings(tpl, chain, (siblings, index) => {
    const next = [...siblings];
    next[index] = fn(next[index]);
    return next;
  });
}

/** Is this manifest a container that accepts children? */
export function isContainer(manifest: BlockManifest | undefined): boolean {
  return !!manifest?.slots && manifest.slots.length > 0;
}

/** Every block id already used anywhere in the template — so a new one can avoid collisions. */
export function collectIds(tpl: Template): Set<string> {
  const ids = new Set<string>();
  const walk = (blocks: BlockInstanceT[] | undefined) => {
    for (const b of blocks ?? []) { if (b.id) ids.add(b.id); walk(b.children); }
  };
  for (const p of tpl.pages ?? []) walk(p.blocks);
  for (const b of tpl.chrome?.header ?? []) ids.add(b.id);
  for (const b of tpl.chrome?.footer ?? []) ids.add(b.id);
  return ids;
}

/** A slug base for a block id from its type: `KpiTileRow` -> `kpitilerow`. */
function slug(type: string): string {
  return type.replace(/[^A-Za-z0-9]/g, '').toLowerCase() || 'block';
}

/**
 * A new block of a type, ready to drop into a template.
 *
 * Props take their manifest DEFAULT where one exists, and a type-appropriate empty otherwise —
 * so a block is never missing a required prop's key. Required BINDINGS take their manifest
 * `suggestion`, which is a real data path (`campaign.clientName`): a block added this way
 * RESOLVES against sample data and renders immediately, rather than dropping in broken and
 * blanking the preview until every binding is typed by hand.
 */
export function makeBlock(manifest: BlockManifest, existingIds: Set<string>): BlockInstanceT {
  const base = slug(manifest.type);
  let n = 1;
  let id = `${base}-${n}`;
  while (existingIds.has(id)) { n++; id = `${base}-${n}`; }

  const props: Record<string, unknown> = {};
  for (const [name, spec] of Object.entries(manifest.props ?? {})) {
    if (spec.default !== undefined) props[name] = spec.default;
    else if (spec.required) props[name] = emptyForType(spec.type);
  }

  const bindings: Record<string, string> = {};
  for (const [name, spec] of Object.entries(manifest.bindings ?? {})) {
    if (spec.required && spec.suggestion) bindings[name] = spec.suggestion;
  }

  const block: BlockInstanceT = { id, type: manifest.type, props, bindings };
  if (isContainer(manifest)) block.children = [];
  return block;
}

function emptyForType(type: string): unknown {
  switch (type) {
    case 'number': return 0;
    case 'boolean': return false;
    case 'array': return [];
    case 'object': return {};
    default: return '';
  }
}
